import { Add as AddIcon } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ComputerIcon from '@mui/icons-material/Computer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Skeleton,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePricing } from '../../hooks/usePricing';
import { useTranslation } from '../../hooks/useTranslation';
import { useTransactionService } from '../../services/transaction.service';
import { convertCentsToEuros } from '../../services/transformers';
import PageLayout from '../common/PageLayout';
import { AdditionalFeatures } from './components/AdditionalFeatures';
import { AddonDialog } from './components/AddonDialog';
import { OrderSummary } from './components/OrderSummary';
import { PackageSelection } from './components/PackageSelection';
import { PlanPageStep, usePlansPage } from './hooks/usePlansPage';
import { calculateNewValidUntil, formatValidUntil } from './utils';

const PlansPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const { pricing, isLoading: isPricingLoading } = usePricing(); // Fetch pricing data
    const hasScrolledRef = useRef(false);

    // Early check for onboarding to prevent flash
    const isOnboardingFromUrl = searchParams.has('onboarding');
    const {
        // State
        selectedTaxYears,
        addons,
        additionalTransactions,
        loading,
        error,
        selectedAddon,
        localTaxYears,
        hasSelections,
        isLoadingTaxYears,
        isLoadingUserPlans,
        userPlans,
        taxReports,
        setSelectedAddon,
        year,
        transactions,
        isLoadingDiscount,
        celloDiscount,

        // Helper functions
        isExpertReviewPurchased,
        isAddonPurchased,
        getAddonValidUntil,

        // Event handlers
        handleTaxYearToggle,
        handlePackageSelect,
        handleExpertReviewToggle,
        handleCheckout,
        handleAddonClick,
        handleAddonToggle,
        handleAddonPurchase,
        handleSelectDiy,
        handleSelectExpert,
        handleBackToInitialOptions,
        handleOnboardingYearChange,
        handleOnboardingYearsChange,
        handleOnboardingTransactionsChange,
        handleOnboardingDiySubmit,
        planPageStep,
        handleIndividualSetupRequest,
        isIndividualSetupLoading,
        individualSetupError,
        isFromOnboarding,
        onboardingYear,
        onboardingYears,
        onboardingTransactions,
        canPurchaseWeDoEverything,
        isIndividualPaymentLink,
        weDoEverythingCustomPricing,
        isLoadingWeDoEverythingCustomPricing,
        weDoEverythingCustomPricingError,
    } = usePlansPage();

    // Create a map to store transaction counts for each year
    const [transactionCounts, setTransactionCounts] = useState<Record<string, number>>({});
    const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);

    const transactionService = useTransactionService();

    // Fetch transaction stats for each tax year
    useEffect(() => {
        // Create a flag to track if the component is still mounted
        let isMounted = true;

        const fetchTransactionStats = async () => {
            // Don't fetch if there are no selected tax years
            if (Object.keys(selectedTaxYears).length === 0) return;

            // Don't set loading if we already have the data for all years
            const missingYears = Object.keys(selectedTaxYears).filter((year) => transactionCounts[year] === undefined);

            if (missingYears.length === 0) return;

            if (isMounted) setIsLoadingTransactions(true);
            const counts = { ...transactionCounts };

            // Only process if we have tax reports
            if (taxReports && taxReports.length > 0) {
                // Process only years we don't have data for yet
                for (const yearKey of missingYears) {
                    const taxYearReport = taxReports.find((report) => report.tax_year === yearKey);
                    if (taxYearReport && taxYearReport.id) {
                        try {
                            // Fetch transaction stats for this year and report ID
                            const stats = await transactionService.getStats(yearKey, taxYearReport.id);
                            counts[yearKey] = stats.totalTransactions || 0;
                        } catch (error) {
                            console.error(`Error fetching stats for year ${yearKey}:`, error);
                            counts[yearKey] = 0;
                        }
                    } else {
                        counts[yearKey] = 0;
                    }
                }

                if (isMounted) setTransactionCounts(counts);
            }
            if (isMounted) setIsLoadingTransactions(false);
        };

        fetchTransactionStats();

        // Cleanup function to prevent state updates if the component unmounts
        return () => {
            isMounted = false;
        };
    }, [selectedTaxYears, taxReports, transactionService]);

    // Always display all tax years, but for onboarding users with selected tax years, create synthetic years
    const displayedYears = useMemo(() => {
        if (isFromOnboarding && Object.keys(selectedTaxYears).length > 0) {
            // For onboarding users who have selected tax years, create synthetic tax year objects
            // Use a default transaction count since we removed the transaction field
            return Object.keys(selectedTaxYears).map((year) => ({
                year: parseInt(year, 10),
                transactions: 100, // Default transaction count for onboarding
            }));
        }
        return localTaxYears;
    }, [isFromOnboarding, selectedTaxYears, localTaxYears]);

    // Debug component to show user plans data
    const DebugUserPlans = () => {
        if (process.env.NODE_ENV !== 'production') {
            return (
                <Box sx={{ mt: 4, p: 2, border: '1px dashed grey', display: 'none' }}>
                    <Typography variant="h6">Debug: User Plans Data</Typography>
                    <pre>{JSON.stringify(userPlans, null, 2)}</pre>
                    <Typography variant="h6">Debug: Tax Years Data</Typography>
                    <pre>{JSON.stringify(localTaxYears, null, 2)}</pre>
                </Box>
            );
        }

        return null;
    };

    // Get the year parameter for use in effects
    const yearParamForEffect = searchParams.get('year');
    useEffect(() => {
        // This effect runs when yearParamForEffect (the actual year value from URL) changes.
        // We reset the scroll lock so that if the user navigates
        // from /plans?year=2023 to /plans?year=2024, it will scroll again.
        hasScrolledRef.current = false;
    }, [yearParamForEffect]);
    // Scroll to year if specified in URL, data is loaded, and not already scrolled
    useEffect(() => {
        if (
            yearParamForEffect &&
            !isLoadingTaxYears &&
            !isLoadingUserPlans &&
            displayedYears &&
            displayedYears.length > 0 &&
            !hasScrolledRef.current
        ) {
            const elementId = `year-${yearParamForEffect}`;
            const yearElement = document.getElementById(elementId);

            if (yearElement) {
                setTimeout(() => {
                    yearElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    hasScrolledRef.current = true;
                }, 100);
            }
        }
    }, [yearParamForEffect, isLoadingTaxYears, isLoadingUserPlans, displayedYears, searchParams]);

    const navigate = useNavigate();

    // Helper function to get dynamic transaction feature text
    const getTransactionFeatureText = (addonType: 'weDoEverything' | 'individualSetup') => {
        if (addonType === 'weDoEverything') {
            const transactionCount = pricing?.addons?.[addonType]?.transactions || 500;
            return t('plansData.addons.weDoEverything.feature4Dynamic', { count: transactionCount });
        } else {
            return t('plansData.addons.individualSetup.feature4');
        }
    };

    // Generate year options for onboarding with progressive disclosure
    const currentYear = new Date().getFullYear();
    const [visibleYearCount, setVisibleYearCount] = useState(4); // Start with 4 years (current + 3 past)
    const [yearTransactionEstimates, setYearTransactionEstimates] = useState<Record<number, string>>({});

    const allYearOptions = useMemo(() => {
        const years = [];
        // Generate more years than we'll initially show (current + 11 past = 12 total)
        for (let i = 0; i < 12; i++) {
            years.push(currentYear - i);
        }
        return years;
    }, [currentYear]);

    const yearOptions = useMemo(() => {
        return allYearOptions.slice(0, visibleYearCount);
    }, [allYearOptions, visibleYearCount]);

    const canShowMoreYears = visibleYearCount < allYearOptions.length;

    const handleShowMoreYears = () => {
        setVisibleYearCount((prev) => Math.min(prev + 4, allYearOptions.length));
    };

    if (isLoadingTaxYears || isLoadingUserPlans || (isIndividualPaymentLink && isLoadingWeDoEverythingCustomPricing)) {
        // Use onboarding layout for loading if coming from onboarding
        if (isOnboardingFromUrl) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                        padding: 3,
                        bgcolor: 'background.default',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '1200px',
                            display: 'flex',
                            flexDirection: { xs: 'column', lg: 'row' },
                            gap: 4,
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box sx={{ flex: 1, width: '100%' }}>
                            {/* Title skeleton */}
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <Skeleton variant="text" width="60%" height={48} sx={{ mx: 'auto' }} />
                            </Box>

                            {/* Two option cards skeleton */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 3,
                                    justifyContent: 'center',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    maxWidth: '800px',
                                    mx: 'auto',
                                }}
                            >
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={200}
                                    sx={{
                                        borderRadius: 2,
                                        maxWidth: { sm: '350px' },
                                    }}
                                />
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={200}
                                    sx={{
                                        borderRadius: 2,
                                        maxWidth: { sm: '350px' },
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Right side skeleton - only show if not on initial step */}
                        {planPageStep !== 'initial' && (
                            <Box sx={{ width: { xs: '100%', lg: '400px' }, flexShrink: 0 }}>
                                <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2 }} />
                            </Box>
                        )}
                    </Box>
                </Box>
            );
        }

        return (
            <PageLayout title={t('plans.title')}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={12} md={7} lg={7}>
                        <Box
                            sx={{
                                p: { xs: 3, sm: 4 },
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                mb: 2,
                            }}
                        >
                            {/* Title skeleton */}
                            <Box sx={{ textAlign: 'center', mb: 6 }}>
                                <Skeleton variant="text" width="60%" height={48} sx={{ mx: 'auto' }} />
                            </Box>

                            {/* Two option cards skeleton */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'center',
                                    gap: 4,
                                    width: '100%',
                                    maxWidth: '700px',
                                    mx: 'auto',
                                }}
                            >
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={200}
                                    sx={{
                                        borderRadius: 2,
                                        flex: 1,
                                        minHeight: '200px',
                                    }}
                                />
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={200}
                                    sx={{
                                        borderRadius: 2,
                                        flex: 1,
                                        minHeight: '200px',
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    {/* No right side skeleton for initial step */}
                </Grid>
            </PageLayout>
        );
    }

    // Modern card styles for both options
    const optionBoxStyle = {
        flex: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        position: 'relative',
        minHeight: '200px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
            borderColor: 'primary.main',
            '& .option-icon': {
                transform: 'scale(1.1)',
                color: 'primary.main',
            },
            '& .option-title': {
                color: 'primary.main',
            },
        },
    };

    const expertBoxes = () => {
        // For individual payment links, show modified weDoEverything box only
        const featuresForWeDoEverything = isIndividualPaymentLink
            ? [
                  t('plansData.addons.weDoEverything.feature1'),
                  t('plansData.addons.weDoEverything.feature2'),
                  t('plansData.addons.weDoEverything.feature3'),
                  // Skip the 4th feature (transaction limit) for individual payment links
                  t('plansData.addons.weDoEverything.feature5'),
                  t('plansData.addons.weDoEverything.feature6'),
              ]
            : [
                  t('plansData.addons.weDoEverything.feature1'),
                  t('plansData.addons.weDoEverything.feature2'),
                  t('plansData.addons.weDoEverything.feature3'),
                  getTransactionFeatureText('weDoEverything'),
                  t('plansData.addons.weDoEverything.feature5'),
                  t('plansData.addons.weDoEverything.feature6'),
              ];

        // For individual payment links, create custom price badge text
        const getPriceBadgeText = () => {
            if (isPricingLoading) {
                return t('loading', 'Loading...');
            }

            if (isIndividualPaymentLink && weDoEverythingCustomPricing?.weDoEverything?.price) {
                const price = `${convertCentsToEuros(weDoEverythingCustomPricing.weDoEverything.price)} ${weDoEverythingCustomPricing.weDoEverything.currency}`;
                return `Individueller Preis ${price}`;
            }

            if (weDoEverythingCustomPricing?.weDoEverything?.price) {
                return `${convertCentsToEuros(weDoEverythingCustomPricing.weDoEverything.price)} ${weDoEverythingCustomPricing.weDoEverything.currency}`;
            }

            if (pricing?.addons?.weDoEverything?.price) {
                return `${convertCentsToEuros(pricing?.addons?.weDoEverything?.price)} EUR`;
            }

            return t('plans.expertOptions.priceUnavailable');
        };

        return (
            <>
                {/* Fixed Price Option - Modern Card Design */}
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: addons.weDoEverything ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        boxShadow: addons.weDoEverything
                            ? '0 8px 25px rgba(25, 118, 210, 0.25)'
                            : '0 2px 8px rgba(0,0,0,0.04)',
                        position: 'relative',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: addons.weDoEverything
                                ? '0 12px 35px rgba(25, 118, 210, 0.35)'
                                : '0 8px 25px rgba(0,0,0,0.12)',
                            borderColor: 'primary.main',
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                        },
                        mb: 4,
                    }}
                    onClick={() => {
                        const isDisabled = isPricingLoading || !canPurchaseWeDoEverything();

                        if (!isDisabled) {
                            handleAddonToggle('weDoEverything');
                        }
                    }}
                >
                    <Box sx={{ p: 4 }}>
                        {/* Price Badge */}
                        <Box
                            sx={{
                                display: 'inline-block',
                                bgcolor: addons.weDoEverything ? 'primary.main' : 'primary.main',
                                color: addons.weDoEverything ? 'primary.contrastText' : 'primary.contrastText',
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                mb: 3,
                                fontWeight: 'bold',
                                fontSize: '1.25rem',
                            }}
                        >
                            {getPriceBadgeText()}
                        </Box>

                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: 'inherit' }}>
                            {t('plansData.addons.weDoEverything.name')}
                        </Typography>

                        <Box sx={{ mb: 4 }}>
                            {featuresForWeDoEverything.map((feature, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        mb: 2,
                                        '&:last-child': { mb: 0 },
                                    }}
                                >
                                    <CheckCircleIcon
                                        sx={{
                                            mr: 2,
                                            fontSize: '1.25rem',
                                            color: 'success.main',
                                            mt: 0.25,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 'normal',
                                            lineHeight: 1.6,
                                            color: 'inherit',
                                            fontSize: '1.1rem',
                                        }}
                                    >
                                        {feature}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Only show note for non-individual payment links */}
                        {!isIndividualPaymentLink && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: '0.95rem', fontStyle: 'italic', fontWeight: 'bold' }}
                            >
                                {t('plansData.addons.weDoEverything.note', {
                                    count:
                                        weDoEverythingCustomPricing?.weDoEverything?.transactions ||
                                        pricing?.addons?.weDoEverything?.transactions ||
                                        500,
                                })}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Individual Price Option - Modern Card Design - Hidden for individual payment links */}
                {!isIndividualPaymentLink && (
                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            position: 'relative',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                borderColor: 'primary.main',
                                '& .price-badge': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                },
                            },
                        }}
                        onClick={() => {
                            if (!(isIndividualSetupLoading || isPricingLoading)) {
                                handleIndividualSetupRequest();
                            }
                        }}
                    >
                        <Box sx={{ p: 4 }}>
                            {/* Price Badge */}
                            <Box
                                className="price-badge"
                                sx={{
                                    display: 'inline-block',
                                    bgcolor: 'secondary.main',
                                    color: 'secondary.contrastText',
                                    px: 3,
                                    py: 1,
                                    borderRadius: 2,
                                    mb: 3,
                                    fontWeight: 'bold',
                                    fontSize: '1.25rem',
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                {t('plans.expertOptions.individualPrice')}
                            </Box>

                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: 'inherit' }}>
                                {t('plans.expertOptions.customSolution')}
                            </Typography>

                            <Box sx={{ mb: 4 }}>
                                {[
                                    t('plansData.addons.individualSetup.feature1'),
                                    t('plansData.addons.individualSetup.feature2'),
                                    t('plansData.addons.individualSetup.feature3'),
                                    getTransactionFeatureText('individualSetup'),
                                    t('plansData.addons.individualSetup.feature5'),
                                    t('plansData.addons.individualSetup.feature6'),
                                ].map((feature, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            mb: 2,
                                            '&:last-child': { mb: 0 },
                                        }}
                                    >
                                        <CheckCircleIcon
                                            sx={{
                                                mr: 2,
                                                fontSize: '1.25rem',
                                                color: 'success.main',
                                                mt: 0.25,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 'normal',
                                                lineHeight: 1.6,
                                                color: 'inherit',
                                                fontSize: '1.1rem',
                                            }}
                                        >
                                            {feature}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: '0.95rem', fontStyle: 'italic', fontWeight: 'bold' }}
                            >
                                {t('plansData.addons.individualSetup.note')}
                            </Typography>

                            {(isPricingLoading || isIndividualSetupLoading) && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <CircularProgress size={24} color="inherit" />
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </>
        );
    };

    // Individual payment link mode - show only "We Do Everything" plan with custom pricing
    if (isIndividualPaymentLink) {
        // If custom pricing failed to load, fall back to normal plans page
        if (weDoEverythingCustomPricingError || !weDoEverythingCustomPricing) {
            console.log('Individual payment link: Custom pricing failed to load, falling back to normal plans page');
            // Continue to normal plans page logic below
        } else {
            // Show individual payment link page with expert choice UI
            return (
                <PageLayout title={t('plans.title')}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={12} md={7} lg={7}>
                            <Box
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    mb: 2,
                                }}
                            >
                                <Typography variant="h6" sx={{ mb: 5, fontWeight: 'bold' }}>
                                    {t('plans.expertOptions.title')}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        gap: 3,
                                        width: '100%',
                                        maxWidth: '600px',
                                    }}
                                >
                                    {expertBoxes()}
                                </Box>
                                {individualSetupError && (
                                    <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
                                        {typeof individualSetupError === 'string'
                                            ? individualSetupError
                                            : (individualSetupError as Error)?.message || t('common.error.unknown')}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={12} md={5} lg={5}>
                            <Box
                                sx={{
                                    position: { md: 'sticky' },
                                    top: { md: '24px' },
                                    width: '100%',
                                }}
                            >
                                <OrderSummary
                                    hasSelections={hasSelections}
                                    selectedTaxYears={{}}
                                    additionalTransactions={{}}
                                    addons={addons}
                                    loading={loading}
                                    error={error}
                                    onCheckout={handleCheckout}
                                    isAddonPurchased={isAddonPurchased}
                                    getAddonValidUntil={getAddonValidUntil}
                                    celloDiscount={celloDiscount}
                                    celloDiscountLoading={isLoadingDiscount}
                                    weDoEverythingCustomPricing={weDoEverythingCustomPricing?.weDoEverything}
                                    planPageStep={planPageStep}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </PageLayout>
            );
        }
    }

    if (localTaxYears.length === 0 && !isFromOnboarding) {
        return (
            <PageLayout title={t('plans.title')}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={12} md={7} lg={7}>
                        <Box
                            sx={{
                                p: { xs: 2, sm: 3, md: 4 },
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 6,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                {t('plans.noYearsAvailable.title')}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', maxWidth: 450 }}>
                                {isFromOnboarding
                                    ? t('plans.noYearsAvailable.onboardingDescription')
                                    : t('plans.noYearsAvailable.description')}
                            </Typography>
                            {isFromOnboarding ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate('/integrations')}
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    {t('plans.noYearsAvailable.addTransactions')}
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate('/integrations')}
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    {t('plans.noYearsAvailable.cta')}
                                </Button>
                            )}
                        </Box>
                    </Grid>

                    {/* Right side - Order Summary (empty) - Hidden for consistency */}
                    {/* <Grid item xs={12} sm={12} md={5} lg={5}>
                        <Box
                            sx={{
                                position: { md: 'sticky' },
                                top: { md: '24px' },
                                width: '100%',
                            }}
                        >
                            <OrderSummary
                                hasSelections={false}
                                selectedTaxYears={{}}
                                additionalTransactions={{}}
                                addons={addons}
                                loading={false}
                                error={null}
                                onCheckout={handleCheckout}
                                isAddonPurchased={isAddonPurchased}
                                getAddonValidUntil={getAddonValidUntil}
                                celloDiscount={celloDiscount}
                                celloDiscountLoading={isLoadingDiscount}
                                weDoEverythingCustomPricing={weDoEverythingCustomPricing?.weDoEverything}
                            />
                        </Box>
                    </Grid> */}
                </Grid>
            </PageLayout>
        );
    }

    const renderLeftSelection = (step: PlanPageStep) => {
        switch (step) {
            case 'initial':
                return (
                    <Box
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                mb: 6,
                                fontWeight: 'bold',
                                textAlign: 'center',
                                color: 'text.primary',
                            }}
                        >
                            {t('plans.initialOptions.title')}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'center',
                                gap: 4,
                                width: '100%',
                                maxWidth: '700px',
                                mx: 'auto',
                            }}
                        >
                            <Box sx={optionBoxStyle} onClick={handleSelectDiy}>
                                <Box
                                    sx={{
                                        p: 4,
                                        textAlign: 'center',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Box sx={{ mb: 3 }}>
                                        <ComputerIcon
                                            className="option-icon"
                                            sx={{
                                                fontSize: '3rem',
                                                color: 'text.secondary',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        className="option-title"
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 2,
                                            color: 'text.primary',
                                            transition: 'color 0.3s ease',
                                        }}
                                    >
                                        {t('plans.initialOptions.doItYourself')}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'text.secondary',
                                            lineHeight: 1.6,
                                            fontSize: '1rem',
                                        }}
                                    >
                                        {t(
                                            'plans.initialOptions.doItYourselfDescription',
                                            'Berechne deine Krypto-Steuern selbstständig mit unserer Plattform'
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    ...optionBoxStyle,
                                    opacity: !canPurchaseWeDoEverything() ? 0.5 : 1,
                                    cursor: !canPurchaseWeDoEverything() ? 'default' : 'pointer',
                                    '&:hover': !canPurchaseWeDoEverything() ? {
                                        transform: 'none',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                        borderColor: 'divider',
                                        '& .option-icon': {
                                            transform: 'none',
                                            color: 'text.secondary',
                                        },
                                        '& .option-title': {
                                            color: 'text.primary',
                                        },
                                    } : optionBoxStyle['&:hover']
                                }}
                                onClick={() => {
                                    if (canPurchaseWeDoEverything()) {
                                        handleSelectExpert();
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        p: 4,
                                        textAlign: 'center',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Box sx={{ mb: 3 }}>
                                        <SupportAgentIcon
                                            className="option-icon"
                                            sx={{
                                                fontSize: '3rem',
                                                color: 'text.secondary',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        className="option-title"
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 2,
                                            color: 'text.primary',
                                            transition: 'color 0.3s ease',
                                        }}
                                    >
                                        {t('plans.initialOptions.withExpert')}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'text.secondary',
                                            lineHeight: 1.6,
                                            fontSize: '1rem',
                                        }}
                                    >
                                        {t(
                                            'plans.initialOptions.withExpertDescription',
                                            'Lass unsere Experten deine Krypto-Steuerberechnung professionell erstellen'
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                );
            case 'expertChoice':
                return (
                    <Box>
                        <Box
                            sx={{
                                p: { xs: 3, sm: 4 },
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                mb: 2,
                            }}
                        >
                            {/* Header with Back button and Title on same line */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 6,
                                    width: '100%',
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={handleBackToInitialOptions}
                                    startIcon={<ArrowBackIcon />}
                                    sx={{
                                        minWidth: 120,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                    }}
                                >
                                    {t('common.back')}
                                </Button>

                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: 'text.primary',
                                        flex: 1,
                                        textAlign: 'center',
                                        mx: 2, // Add some margin to prevent overlap
                                    }}
                                >
                                    {t('plans.expertOptions.title')}
                                </Typography>

                                {/* Invisible spacer to balance the layout */}
                                <Box sx={{ minWidth: 120 }} />
                            </Box>

                            {expertBoxes()}

                            {individualSetupError && (
                                <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
                                    {typeof individualSetupError === 'string'
                                        ? individualSetupError
                                        : (individualSetupError as Error)?.message || t('common.error.unknown')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            case 'onboardingDiyInput':
                return (
                    <Box
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            mb: 2,
                            maxWidth: 600,
                            mx: 'auto',
                        }}
                    >
                        <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                            {t('plans.onboardingDiy.title')}
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 5, textAlign: 'center', lineHeight: 1.6 }}
                        >
                            {t('plans.onboardingDiy.description')}
                        </Typography>

                        <Box sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {yearOptions.map((year) => {
                                    const isSelected = onboardingYears.includes(year.toString());
                                    return (
                                        <Box key={year}>
                                            {/* Year Selection Card */}
                                            <Box
                                                onClick={() => {
                                                    const yearStr = year.toString();
                                                    const newYears = isSelected
                                                        ? onboardingYears.filter((y) => y !== yearStr)
                                                        : [...onboardingYears, yearStr];
                                                    handleOnboardingYearsChange(newYears);
                                                }}
                                                sx={{
                                                    p: 3,
                                                    border: '2px solid',
                                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    bgcolor: isSelected
                                                        ? 'rgba(25, 118, 210, 0.08)'
                                                        : 'background.paper',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: isSelected
                                                            ? '0 8px 25px rgba(25, 118, 210, 0.25)'
                                                            : '0 8px 25px rgba(0,0,0,0.12)',
                                                        borderColor: 'primary.main',
                                                    },
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    boxShadow: isSelected
                                                        ? '0 4px 20px rgba(25, 118, 210, 0.15)'
                                                        : '0 2px 8px rgba(0,0,0,0.04)',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: '4px',
                                                            border: '2px solid',
                                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                                            bgcolor: isSelected ? 'primary.main' : 'transparent',
                                                            color: isSelected ? 'white' : 'transparent',
                                                            mr: 3,
                                                            transition: 'all 0.2s ease',
                                                        }}
                                                    >
                                                        {isSelected && (
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    lineHeight: 1,
                                                                    fontWeight: 'bold',
                                                                }}
                                                            >
                                                                ✓
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                        {year}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ fontWeight: 'medium' }}
                                                >
                                                    Steuerjahr
                                                </Typography>
                                            </Box>

                                            {/* Transaction Estimate Dropdown - Only show if year is selected */}
                                            {isSelected && (
                                                <Box sx={{ mt: 2, ml: 4 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}
                                                    >
                                                        Geschätzte Anzahl Transaktionen in {year}:
                                                    </Typography>
                                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                                        <Select
                                                            value={yearTransactionEstimates[year] || ''}
                                                            onChange={(e) => {
                                                                setYearTransactionEstimates((prev) => ({
                                                                    ...prev,
                                                                    [year]: e.target.value,
                                                                }));
                                                            }}
                                                            displayEmpty
                                                            sx={{
                                                                borderRadius: 1,
                                                                bgcolor: 'background.paper',
                                                            }}
                                                        >
                                                            <MenuItem value="" disabled>
                                                                <em>Bitte auswählen</em>
                                                            </MenuItem>
                                                            <MenuItem value="25">Bis 50 Transaktionen (Basic)</MenuItem>
                                                            <MenuItem value="125">
                                                                51 - 200 Transaktionen (Standard)
                                                            </MenuItem>
                                                            <MenuItem value="600">
                                                                201 - 1000 Transaktionen (Pro)
                                                            </MenuItem>
                                                            <MenuItem value="5000">
                                                                Über 1000 Transaktionen (Enterprise)
                                                            </MenuItem>
                                                            <MenuItem
                                                                value="skip"
                                                                sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                                                            >
                                                                Weiß ich nicht (überspringen)
                                                            </MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* Show more years button */}
                            {canShowMoreYears && (
                                <Box sx={{ textAlign: 'center', mt: 3 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleShowMoreYears}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'medium',
                                            borderColor: 'divider',
                                            color: 'text.secondary',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                color: 'primary.main',
                                            },
                                        }}
                                    >
                                        + Weitere Jahre anzeigen
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        {error && (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    bgcolor: 'error.light',
                                    mb: 3,
                                    textAlign: 'center',
                                }}
                            >
                                <Typography color="error.dark" sx={{ fontWeight: 'medium' }}>
                                    {error}
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleBackToInitialOptions}
                                sx={{
                                    minWidth: 120,
                                    fontWeight: 600,
                                    borderRadius: 2,
                                }}
                            >
                                {t('common.back')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    const transactionEstimates: Record<string, number> = {};
                                    const yearsToInclude: string[] = [];

                                    onboardingYears.forEach((year) => {
                                        const estimate = yearTransactionEstimates[parseInt(year)];
                                        if (estimate && estimate !== 'skip') {
                                            transactionEstimates[year] = parseInt(estimate);
                                            yearsToInclude.push(year);
                                        }
                                        // If estimate is 'skip', we don't include this year at all
                                    });

                                    // If no years have estimates (all skipped), redirect to dashboard
                                    if (yearsToInclude.length === 0) {
                                        navigate('/dashboard');
                                        return;
                                    }

                                    // Pass only years with actual estimates
                                    handleOnboardingDiySubmit(transactionEstimates, yearsToInclude);
                                }}
                                disabled={(() => {
                                    if (onboardingYears.length === 0) return true;

                                    const missingEstimates = onboardingYears.some(
                                        (year) => !yearTransactionEstimates[parseInt(year)]
                                    );
                                    if (missingEstimates) return true;

                                    return false;
                                })()}
                                sx={{
                                    minWidth: 180,
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    fontSize: '1rem',
                                }}
                            >
                                {(() => {
                                    if (onboardingYears.length === 0) {
                                        return t('plans.onboardingDiy.selectYear', 'Jahr auswählen');
                                    }

                                    const missingEstimates = onboardingYears.some(
                                        (year) => !yearTransactionEstimates[parseInt(year)]
                                    );
                                    if (missingEstimates) {
                                        return t('plans.onboardingDiy.transactions', 'Transaktionen angeben');
                                    }

                                    // Check if all years are skipped
                                    const allYearsSkipped = onboardingYears.every(
                                        (year) => yearTransactionEstimates[parseInt(year)] === 'skip'
                                    );
                                    if (allYearsSkipped) {
                                        return t('plans.onboardingDiy.goToApp', 'Zur App gehen');
                                    }

                                    // Count years that are not skipped
                                    const yearsWithEstimates = onboardingYears.filter(
                                        (year) => yearTransactionEstimates[parseInt(year)] !== 'skip'
                                    );
                                    const yearText =
                                        yearsWithEstimates.length === 1
                                            ? t('plans.onboardingDiy.year', 'Jahr')
                                            : t('plans.onboardingDiy.years', 'Jahren');
                                    return t(
                                        'plans.onboardingDiy.continueWithYears',
                                        'Weiter mit {{count}} {{yearText}}',
                                        {
                                            count: yearsWithEstimates.length,
                                            yearText,
                                        }
                                    );
                                })()}
                            </Button>
                        </Box>
                    </Box>
                );
            case 'diy':
                return (
                    <>
                        {/* Tax Years and Package Selection */}
                        <Box
                            sx={{
                                p: { xs: 3, sm: 4 },
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                mb: 3,
                            }}
                        >
                            {/* Instructions text with Back Button */}
                            <Box
                                sx={{
                                    mb: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={handleBackToInitialOptions}
                                    sx={{
                                        minWidth: 100,
                                        fontWeight: 600,
                                        flexShrink: 0,
                                    }}
                                >
                                    {t('common.back')}
                                </Button>
                                <Box
                                    sx={{
                                        flex: 1,
                                        p: 3,
                                        borderRadius: 2,
                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            mr: 1.5,
                                            color: 'primary.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <InfoOutlinedIcon fontSize="medium" />
                                    </Box>
                                    <Typography variant="body1" fontWeight="medium">
                                        {t('plans.instructions.title')}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Package Selection for each year */}
                            {displayedYears.map((ty) => {
                                const isSelected = !!selectedTaxYears[ty.year];
                                // For onboarding users, use the transaction count from their input, otherwise use API data
                                const transactionCount =
                                    isFromOnboarding && ty.transactions !== undefined
                                        ? ty.transactions
                                        : transactionCounts[ty.year] || 0;
                                const yearData = selectedTaxYears[ty.year] || { package: null, expertReview: false };
                                const shouldRender = !isLoadingTransactions || transactionCount > 0;

                                return (
                                    <Box
                                        id={`year-${ty.year}`}
                                        key={ty.year}
                                        sx={{
                                            mb: 4,
                                            border: '2px solid',
                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            boxShadow: isSelected
                                                ? '0 4px 20px rgba(25, 118, 210, 0.15)'
                                                : '0 2px 8px rgba(0,0,0,0.04)',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: isSelected
                                                    ? '0 8px 30px rgba(25, 118, 210, 0.25)'
                                                    : '0 8px 25px rgba(0,0,0,0.12)',
                                            },
                                        }}
                                    >
                                        {/* Tax Year Header */}
                                        <Box
                                            onClick={() => handleTaxYearToggle(ty.year.toString())}
                                            sx={{
                                                p: 3,
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                bgcolor: 'background.paper',
                                                transition: 'background-color 0.2s ease',
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: '4px',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                                                    color: isSelected ? 'white' : 'transparent',
                                                    mr: 2,
                                                }}
                                            >
                                                {isSelected && (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            fontSize: '16px',
                                                            lineHeight: 1,
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        ✓
                                                    </Box>
                                                )}
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                                                {t('plans.taxYear', { year: ty.year })}
                                                {isSelected &&
                                                    !isLoadingTransactions &&
                                                    !isFromOnboarding &&
                                                    `: ${transactionCount} Transaktionen`}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                ab 29,99 €
                                            </Typography>
                                        </Box>

                                        {/* Package Selection - Only show if tax year is selected */}
                                        {isSelected && shouldRender && (
                                            <Box sx={{ p: 1 }}>
                                                <PackageSelection
                                                    year={ty.year.toString()}
                                                    yearData={yearData}
                                                    transactions={transactionCount}
                                                    onPackageSelect={handlePackageSelect}
                                                    onExpertReviewToggle={handleExpertReviewToggle}
                                                    isExpertReviewPurchased={isExpertReviewPurchased}
                                                />
                                            </Box>
                                        )}

                                        {/* Show loading skeleton if needed */}
                                        {isSelected && !shouldRender && (
                                            <Box sx={{ p: 2 }}>
                                                <Skeleton
                                                    variant="rectangular"
                                                    width="100%"
                                                    height={420}
                                                    sx={{ borderRadius: 2 }}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                );
        }
    };

    // Early return for onboarding layout to prevent flash
    if (isOnboardingFromUrl || isFromOnboarding) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    padding: 3,
                    bgcolor: 'background.default',
                }}
            >
                {/* Header with Skip Button */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 24,
                        right: 24,
                    }}
                >
                    <Button
                        variant="text"
                        onClick={() => navigate('/dashboard')}
                        sx={{
                            fontWeight: 500,
                            color: 'text.secondary',
                            textDecoration: 'underline',
                            '&:hover': {
                                color: 'text.primary',
                                backgroundColor: 'transparent',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {t('common.skip')}
                    </Button>
                </Box>

                {/* Centered Content */}
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '1200px',
                        display: 'flex',
                        flexDirection: { xs: 'column', lg: 'row' },
                        gap: 4,
                        alignItems: 'flex-start',
                    }}
                >
                    {/* Left side - Main content */}
                    <Box sx={{ flex: 1, width: '100%' }}>{renderLeftSelection(planPageStep)}</Box>

                    {/* Right side - Order Summary and Additional Features */}
                    {planPageStep !== 'initial' && planPageStep !== 'onboardingDiyInput' && (
                        <Box sx={{ width: { xs: '100%', lg: '400px' }, flexShrink: 0 }}>
                            {planPageStep === 'diy' && (
                                <Box sx={{ mb: 2 }}>
                                    <AdditionalFeatures
                                        addons={addons}
                                        isAddonPurchased={isAddonPurchased}
                                        onAddonClick={handleAddonClick}
                                        onAddonToggle={handleAddonToggle}
                                        getAddonValidUntil={getAddonValidUntil}
                                    />
                                </Box>
                            )}
                            <OrderSummary
                                hasSelections={hasSelections}
                                selectedTaxYears={selectedTaxYears}
                                additionalTransactions={additionalTransactions}
                                addons={addons}
                                loading={loading}
                                error={error}
                                onCheckout={handleCheckout}
                                isAddonPurchased={isAddonPurchased}
                                getAddonValidUntil={getAddonValidUntil}
                                celloDiscount={celloDiscount}
                                celloDiscountLoading={isLoadingDiscount}
                                weDoEverythingCustomPricing={weDoEverythingCustomPricing?.weDoEverything}
                                planPageStep={planPageStep}
                            />
                        </Box>
                    )}
                </Box>

                {/* Addon Dialog */}
                <AddonDialog
                    selectedAddon={selectedAddon}
                    onClose={() => setSelectedAddon(null)}
                    onPurchase={handleAddonPurchase}
                    formatValidUntil={formatValidUntil}
                    calculateNewValidUntil={calculateNewValidUntil}
                />
            </Box>
        );
    }

    // Render normal app layout
    return (
        <PageLayout title={t('plans.title')}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={7} lg={7}>
                    {renderLeftSelection(planPageStep)}
                </Grid>

                {/* Right side - Order Summary and Additional Features - Conditionally rendered */}

                <Grid item xs={12} sm={12} md={5} lg={5} sx={{ position: 'relative' }}>
                    <Box
                        sx={{
                            display: 'block',
                            position: { xs: 'static', md: 'fixed' },
                            width: { md: 'calc(31.666% - 48px)' },
                            maxHeight: { md: 'calc(100vh - 100px)' },
                            overflowY: { md: 'auto' },
                            right: { md: 'auto' },
                            top: '88px',
                        }}
                    >
                        {/* Additional Features */}

                        {planPageStep === 'diy' && (
                            <AdditionalFeatures
                                addons={addons}
                                isAddonPurchased={isAddonPurchased}
                                onAddonClick={handleAddonClick}
                                onAddonToggle={handleAddonToggle}
                                getAddonValidUntil={getAddonValidUntil}
                            />
                        )}

                        {/* Order Summary */}
                        {planPageStep !== 'initial' && planPageStep !== 'onboardingDiyInput' && (
                            <Box sx={{ mt: planPageStep === 'diy' ? 2 : 0 }}>
                                <OrderSummary
                                    hasSelections={hasSelections}
                                    selectedTaxYears={selectedTaxYears}
                                    additionalTransactions={additionalTransactions}
                                    addons={addons}
                                    loading={loading}
                                    error={error}
                                    onCheckout={handleCheckout}
                                    isAddonPurchased={isAddonPurchased}
                                    getAddonValidUntil={getAddonValidUntil}
                                    celloDiscount={celloDiscount}
                                    celloDiscountLoading={isLoadingDiscount}
                                    weDoEverythingCustomPricing={weDoEverythingCustomPricing?.weDoEverything}
                                    planPageStep={planPageStep}
                                />
                                <DebugUserPlans />
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>

            {/* Addon Dialog */}
            <AddonDialog
                selectedAddon={selectedAddon}
                onClose={() => setSelectedAddon(null)}
                onPurchase={handleAddonPurchase}
                formatValidUntil={formatValidUntil}
                calculateNewValidUntil={calculateNewValidUntil}
            />
        </PageLayout>
    );
};

export default PlansPage;
